<template>
  <div class="edit-post-view">
    <!-- Header -->
    <div class="d-flex flex-wrap align-center justify-space-between ga-3 mb-6">
      <div class="d-flex align-center ga-3">
        <v-btn
          :to="{ name: 'BookDetail', params: { id: $route.params.id } }"
          icon
          variant="tonal"
          size="small"
          aria-label="Back"
        >
          <v-icon size="20">mdi-arrow-left</v-icon>
        </v-btn>
        <div>
          <h1 class="text-h4 font-weight-bold">Edit Book</h1>
          <p class="text-caption text-medium-emphasis mb-0">Update book details in your collection</p>
        </div>
      </div>

      <!-- Actions -->
      <div v-if="!loading && !loadError" class="d-flex align-center flex-wrap ga-2">
        <v-btn
          :to="{ name: 'BookDetail', params: { id: $route.params.id } }"
          variant="tonal"
          class="btn-modern"
        >
          Cancel
        </v-btn>

        <v-btn
          color="success"
          class="btn-modern btn-glow-success text-white font-weight-bold"
          :disabled="saving"
          :loading="saving"
          prepend-icon="mdi-content-save-check-outline"
          @click="saveBook"
        >
          Save Changes
        </v-btn>
      </div>
    </div>

    <!-- Loading / Error States -->
    <div v-if="loading" class="text-center py-16">
      <v-progress-circular indeterminate color="success" size="56" width="4" />
      <div class="text-body-2 text-medium-emphasis mt-4">Loading book data...</div>
    </div>

    <v-alert v-else-if="loadError" type="error" class="mb-4" variant="tonal">{{ loadError }}</v-alert>

    <template v-else>
      <v-alert v-if="error" type="error" class="mb-4" closable variant="tonal" @click:close="error = ''">
        {{ error }}
      </v-alert>

      <!-- Main Edit Form -->
      <v-form ref="formRef">
        <v-row>
          <!-- Left Column: Cover Preview -->
          <v-col cols="12" md="4" lg="3">
            <v-card class="glass-card pa-4 text-center sticky-poster-card">
              <div class="text-subtitle-2 font-weight-bold mb-3 text-left">Book Cover</div>

              <div class="poster-dropzone mb-3">
                <v-img
                  v-if="posterPreview"
                  :src="posterPreview"
                  :aspect-ratio="2 / 3"
                  cover
                  class="rounded-lg shadow-md"
                />
                <div v-else class="poster-empty-placeholder">
                  <v-icon size="48" color="#10b981" class="mb-2">mdi-book-outline</v-icon>
                  <div class="text-caption font-weight-bold">No Cover Image</div>
                </div>
              </div>

              <v-file-input
                v-model="imageFile"
                label="Replace Cover Image"
                accept="image/*"
                density="compact"
                hide-details
                prepend-icon=""
                prepend-inner-icon="mdi-upload"
                variant="outlined"
                class="mb-2"
              />

              <v-text-field
                v-model="book.imageurl"
                label="Or Cover URL"
                density="compact"
                hide-details
                variant="outlined"
              />
            </v-card>
          </v-col>

          <!-- Right Column: Sections -->
          <v-col cols="12" md="8" lg="9">
            <!-- Section 1: Details -->
            <v-card class="glass-card pa-6 mb-6">
              <div class="d-flex align-center ga-2 mb-4">
                <v-icon color="#10b981" size="22">mdi-information-outline</v-icon>
                <h3 class="text-subtitle-1 font-weight-bold mb-0">Book Details</h3>
              </div>

              <v-row dense>
                <v-col cols="12" sm="8">
                  <v-text-field
                    v-model="book.title"
                    label="Book Title *"
                    density="comfortable"
                    :rules="[required]"
                  />
                </v-col>
                <v-col cols="12" sm="4">
                  <v-select
                    v-model="book.category"
                    :items="CATEGORIES"
                    label="Category *"
                    density="comfortable"
                    :rules="[required]"
                  />
                </v-col>

                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="book.author"
                    label="Author / Writer"
                    density="comfortable"
                  />
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="book.original"
                    label="Original Title (Optional)"
                    density="comfortable"
                  />
                </v-col>

                <v-col cols="6" sm="3">
                  <v-text-field
                    v-model="book.year"
                    label="Publish Year"
                    density="comfortable"
                  />
                </v-col>
                <v-col cols="6" sm="3">
                  <v-text-field
                    v-model="book.publishedDate"
                    label="Published Date"
                    density="comfortable"
                  />
                </v-col>
                <v-col cols="6" sm="3">
                  <v-text-field
                    v-model="book.pageCount"
                    label="Page Count"
                    density="comfortable"
                  />
                </v-col>
                <v-col cols="6" sm="3">
                  <v-select
                    v-model="book.language"
                    :items="LANGUAGES"
                    label="Language"
                    density="comfortable"
                  />
                </v-col>
                <v-col cols="6" sm="3">
                  <v-text-field
                    v-model="book.type"
                    label="Format / Type"
                    density="comfortable"
                  />
                </v-col>

                <!-- Rating -->
                <v-col cols="12" sm="9" class="d-flex align-center ga-3 mt-2">
                  <span class="text-caption font-weight-bold text-medium-emphasis">Your Rating:</span>
                  <v-rating
                    v-model="ratingNumber"
                    half-increments
                    density="comfortable"
                    color="amber"
                    active-color="amber"
                  />
                  <span class="text-body-2 font-weight-bold">{{ ratingNumber }} / 5</span>
                </v-col>
              </v-row>
            </v-card>

            <!-- Section 2: Description -->
            <v-card class="glass-card pa-6 mb-6">
              <div class="d-flex align-center ga-2 mb-4">
                <v-icon color="#10b981" size="22">mdi-book-open-page-variant-outline</v-icon>
                <h3 class="text-subtitle-1 font-weight-bold mb-0">Summary & Description</h3>
              </div>

              <v-textarea
                v-model="book.description"
                label="Book Description / Synopsis"
                rows="5"
                auto-grow
                density="comfortable"
                variant="outlined"
              />
            </v-card>
          </v-col>
        </v-row>
      </v-form>
    </template>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { CATEGORIES, LANGUAGES } from '@/config/books'
import BooksAPI from '@/services/books'
import { appendBookFormData, defaultBookForm } from '@/utils/bookForm'
import { apiErrorMessage } from '@/utils/formValidate'

export default {
  name: 'EditBookPost',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const formRef = ref(null)
    const book = reactive(defaultBookForm())
    const oldImage = ref('')
    const imageFile = ref(null)
    const loading = ref(true)
    const saving = ref(false)
    const error = ref('')
    const loadError = ref('')

    const required = (value) => !!value || 'This field is required'

    const ratingNumber = computed({
      get: () => Number(book.rating) || 0,
      set: (value) => {
        book.rating = String(value)
      },
    })

    const posterPreview = computed(() => {
      const file = imageFile.value
      if (file) {
        const selected = Array.isArray(file) ? file[0] : file
        if (selected) return URL.createObjectURL(selected)
      }
      if (book.imageurl) return book.imageurl
      if (book.image) return `/uploads/${book.image}`
      return ''
    })

    const load = async () => {
      loading.value = true
      loadError.value = ''
      try {
        const { data } = await BooksAPI.getPostByID(route.params.id)
        Object.assign(book, { ...defaultBookForm(), ...data })
        oldImage.value = data.image || ''
      } catch (err) {
        loadError.value = apiErrorMessage(err, 'Book not found')
      } finally {
        loading.value = false
      }
    }

    const saveBook = async () => {
      error.value = ''
      const title = String(book.title || '').trim()
      const category = String(book.category || '').trim()

      if (!title || !category) {
        error.value = 'Title and category are required before saving.'
        return
      }

      book.title = title
      book.category = category
      saving.value = true
      try {
        const formData = new FormData()
        appendBookFormData(formData, book)
        formData.append('old_image', oldImage.value || '')
        if (imageFile.value) {
          const file = Array.isArray(imageFile.value) ? imageFile.value[0] : imageFile.value
          if (file) formData.append('image', file)
        }
        await BooksAPI.updatePost(route.params.id, formData)
        router.push({ name: 'BookDetail', params: { id: route.params.id } })
      } catch (err) {
        error.value = apiErrorMessage(err, 'Failed to update book')
      } finally {
        saving.value = false
      }
    }

    onMounted(load)

    return {
      formRef,
      book,
      imageFile,
      loading,
      saving,
      error,
      loadError,
      CATEGORIES,
      LANGUAGES,
      required,
      ratingNumber,
      posterPreview,
      saveBook,
    }
  },
}
</script>

<style scoped>
.edit-post-view {
  max-width: 1240px;
  margin: 0 auto;
}

.sticky-poster-card {
  position: sticky;
  top: 80px;
}

.poster-dropzone {
  position: relative;
  aspect-ratio: 2 / 3;
  border-radius: 12px;
  overflow: hidden;
}

.poster-empty-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  border: 2px dashed rgba(128, 128, 128, 0.3);
  border-radius: 12px;
  padding: 16px;
  background: rgba(128, 128, 128, 0.05);
}
</style>

